// src/lib/pdfReportBuilder.ts
// Text-based PDF generation using jsPDF native methods and jspdf-autotable
// Produces small, searchable, text-selectable PDFs

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { NewSizingSnapshot, NewSizingRequest } from '@/sizing/newSizingEngine';
import type { SizingNarrativeData } from '@/types/narrative';
import type { CostComparisonResult, MonthlyRollupEnhanced, CostSimulatorConfig } from '@/lib/costCalculations';
import { format } from 'date-fns';

// Types for PDF data
export interface PdfReportData {
  // Core sizing data
  snapshot: NewSizingSnapshot;
  request: NewSizingRequest;
  narrativeData: SizingNarrativeData | null;
  
  // Display names
  serverDisplayName: string;
  acceleratorDisplayName: string;
  
  // Cost comparison data (optional)
  costData?: {
    config: CostSimulatorConfig;
    comparison: CostComparisonResult;
    monthlyRollups: MonthlyRollupEnhanced[];
  };
}

// Constants
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 15;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

// Colors (as RGB arrays)
const COLORS = {
  primary: [59, 130, 246] as [number, number, number],     // blue-500
  primaryDark: [37, 99, 235] as [number, number, number],  // blue-600
  secondary: [99, 102, 241] as [number, number, number],   // indigo-500
  gray900: [17, 24, 39] as [number, number, number],
  gray700: [55, 65, 81] as [number, number, number],
  gray500: [107, 114, 128] as [number, number, number],
  gray400: [156, 163, 175] as [number, number, number],
  gray100: [243, 244, 246] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  success: [34, 197, 94] as [number, number, number],
  warning: [234, 179, 8] as [number, number, number],
};

// Format helpers
const fmt = (n: number, decimals = 0): string => {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const fmtDec = (n: number, decimals = 2): string => {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
};

const fmtCurrency = (n: number, compact = false): string => {
  if (compact && Math.abs(n) >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(1)}M`;
  }
  if (compact && Math.abs(n) >= 1_000) {
    return `$${(n / 1_000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

const fmtTokens = (value: number): string => {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(0);
};

// PDF helper class
class PdfBuilder {
  private pdf: jsPDF;
  private y: number = MARGIN;
  private pageNum: number = 1;
  
  constructor() {
    this.pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.pdf.setFont('helvetica');
  }
  
  get doc(): jsPDF {
    return this.pdf;
  }
  
  get currentY(): number {
    return this.y;
  }
  
  set currentY(val: number) {
    this.y = val;
  }
  
  checkPageBreak(neededHeight: number): void {
    if (this.y + neededHeight > PAGE_HEIGHT - MARGIN) {
      this.newPage();
    }
  }
  
  newPage(): void {
    this.pdf.addPage();
    this.y = MARGIN;
    this.pageNum++;
    this.addPageHeader();
  }
  
  private addPageHeader(): void {
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(...COLORS.gray400);
    this.pdf.text(`Page ${this.pageNum}`, PAGE_WIDTH - MARGIN, 10, { align: 'right' });
  }
  
  addTitle(text: string, fontSize = 20): void {
    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(...COLORS.gray900);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(text, MARGIN, this.y);
    this.y += fontSize * 0.5;
  }
  
  addSectionHeader(number: string, title: string, subtitle?: string): void {
    this.checkPageBreak(20);
    
    // Blue border line
    this.pdf.setDrawColor(...COLORS.primaryDark);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(MARGIN, this.y, PAGE_WIDTH - MARGIN, this.y);
    this.y += 2;
    
    // Number and title
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(...COLORS.primaryDark);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(number, MARGIN, this.y + 4);
    
    this.pdf.setFontSize(14);
    this.pdf.setTextColor(...COLORS.gray900);
    this.pdf.text(title, MARGIN + 8, this.y + 4);
    this.y += 8;
    
    if (subtitle) {
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(...COLORS.gray500);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(subtitle, MARGIN, this.y);
      this.y += 5;
    }
    
    this.y += 4;
  }
  
  addSubheader(text: string): void {
    this.checkPageBreak(10);
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(...COLORS.gray700);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(text, MARGIN, this.y);
    this.y += 6;
  }
  
  addParagraph(text: string, maxWidth = CONTENT_WIDTH): void {
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...COLORS.gray700);
    this.pdf.setFont('helvetica', 'normal');
    const lines = this.pdf.splitTextToSize(text, maxWidth);
    this.checkPageBreak(lines.length * 4.5);
    this.pdf.text(lines, MARGIN, this.y);
    this.y += lines.length * 4.5;
  }
  
  addFormulaBlock(formulas: string[]): void {
    const blockHeight = formulas.length * 5 + 6;
    this.checkPageBreak(blockHeight);
    
    // Background
    this.pdf.setFillColor(COLORS.gray100[0], COLORS.gray100[1], COLORS.gray100[2]);
    this.pdf.roundedRect(MARGIN, this.y - 1, CONTENT_WIDTH, blockHeight, 2, 2, 'F');
    
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...COLORS.gray700);
    this.pdf.setFont('courier', 'normal');
    
    let fy = this.y + 3;
    for (const formula of formulas) {
      this.pdf.text(formula, MARGIN + 3, fy);
      fy += 5;
    }
    
    this.y += blockHeight + 2;
    this.pdf.setFont('helvetica', 'normal');
  }
  
  addMetricRow(metrics: Array<{ label: string; value: string; highlight?: boolean }>): void {
    const cardWidth = CONTENT_WIDTH / metrics.length - 2;
    this.checkPageBreak(25);
    
    let x = MARGIN;
    for (const metric of metrics) {
      // Card background
      if (metric.highlight) {
        this.pdf.setFillColor(239, 246, 255); // blue-50
        this.pdf.setDrawColor(...COLORS.primary);
      } else {
        this.pdf.setFillColor(...COLORS.white);
        this.pdf.setDrawColor(...COLORS.gray400);
      }
      this.pdf.setLineWidth(0.3);
      this.pdf.roundedRect(x, this.y, cardWidth, 20, 2, 2, 'FD');
      
      // Label
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(...COLORS.gray500);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(metric.label, x + cardWidth / 2, this.y + 6, { align: 'center' });
      
      // Value
      this.pdf.setFontSize(14);
      const textColor = metric.highlight ? COLORS.primary : COLORS.gray900;
      this.pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(metric.value, x + cardWidth / 2, this.y + 15, { align: 'center' });
      
      x += cardWidth + 4;
    }
    
    this.y += 24;
  }
  
  addTable(
    head: string[][],
    body: string[][],
    options?: { startY?: number; columnStyles?: Record<number, { halign?: 'left' | 'center' | 'right' }> }
  ): void {
    autoTable(this.pdf, {
      startY: options?.startY ?? this.y,
      head,
      body,
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { 
        fontSize: 9,
        textColor: COLORS.gray700,
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251], // gray-50
      },
      margin: { left: MARGIN, right: MARGIN },
      columnStyles: options?.columnStyles,
    });
    
    // Update Y position after table
    this.y = (this.pdf as any).lastAutoTable?.finalY ?? this.y + 10;
    this.y += 4;
  }
  
  addKeyValueTable(data: Array<{ key: string; value: string }>): void {
    const body = data.map(row => [row.key, row.value]);
    this.addTable([], body, {
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
      },
    });
  }
  
  addSpace(mm: number): void {
    this.y += mm;
  }
  
  save(filename: string): void {
    this.pdf.save(filename);
  }
}

// Main PDF generation function
export async function generateTextBasedPdf(data: PdfReportData): Promise<void> {
  const builder = new PdfBuilder();
  const { snapshot, request, narrativeData, serverDisplayName, acceleratorDisplayName, costData } = data;
  
  const serverCount = Math.ceil(snapshot.finalGpuCount / 8);
  const timestamp = format(new Date(), 'MMMM d, yyyy');
  
  const bottleneckLabel: Record<string, string> = {
    kv: 'KV Memory',
    prompt: 'Prompt Throughput',
    decode: 'Decode Throughput',
    none: 'None',
  };
  
  // ===== COVER PAGE =====
  addCoverPage(builder, {
    timestamp,
    serverCount,
    gpuCount: snapshot.finalGpuCount,
    concurrentUsers: request.baseConcurrentUsers,
    bottleneck: bottleneckLabel[snapshot.finalBottleneck] ?? 'Unknown',
    serverDisplayName,
    acceleratorDisplayName,
    modelId: request.model.id,
    paramsBillions: request.model.paramsBillions,
    precisionBits: request.model.precisionBits,
    batchSize: request.batchSize,
    conversationTurns: request.conversationTurns,
    latencySloMs: request.latencySloMs,
    personaCount: request.personas.length,
    hasCostData: !!costData,
  });
  
  // ===== SECTION 1: METHODOLOGY =====
  builder.newPage();
  addMethodologySection(builder, snapshot);
  
  // ===== SECTION 2: USER PERSONAS =====
  builder.newPage();
  addPersonaSection(builder, request, snapshot);
  
  // ===== SECTION 3: THROUGHPUT =====
  builder.newPage();
  addThroughputSection(builder, snapshot, request, narrativeData);
  
  // ===== SECTION 4: MEMORY SIZING =====
  builder.newPage();
  addMemorySection(builder, request, snapshot, narrativeData);
  
  // ===== SECTION 5: BOTTLENECK ANALYSIS =====
  builder.newPage();
  addBottleneckSection(builder, snapshot, narrativeData);
  
  // ===== SECTION 6: RECOMMENDATIONS =====
  builder.newPage();
  addRecommendationsSection(builder, {
    serverCount,
    gpuCount: snapshot.finalGpuCount,
    serverDisplayName,
    acceleratorDisplayName,
    bottleneck: bottleneckLabel[snapshot.finalBottleneck] ?? 'Unknown',
  });
  
  // ===== SECTION 7: ADVANCED INSIGHTS (if narrativeData available) =====
  if (narrativeData) {
    builder.newPage();
    addAdvancedInsightsSection(builder, snapshot, narrativeData);
  }
  
  // ===== SECTION 8: COST COMPARISON (if costData available) =====
  if (costData) {
    builder.newPage();
    addCloudCostSection(builder, costData);
    
    builder.newPage();
    addOnPremTcoSection(builder, costData, serverCount);
    
    builder.newPage();
    addCostComparisonSummary(builder, costData);
  }
  
  // Generate filename and save
  const filename = `ai-sizing-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  builder.save(filename);
}

// Section builders
function addCoverPage(
  builder: PdfBuilder,
  data: {
    timestamp: string;
    serverCount: number;
    gpuCount: number;
    concurrentUsers: number;
    bottleneck: string;
    serverDisplayName: string;
    acceleratorDisplayName: string;
    modelId: string;
    paramsBillions: number;
    precisionBits: number;
    batchSize: number;
    conversationTurns: number;
    latencySloMs: number;
    personaCount: number;
    hasCostData: boolean;
  }
): void {
  // Header
  builder.addTitle('AI Infrastructure Sizing Report', 22);
  builder.addSpace(2);
  
  builder.doc.setFontSize(12);
  builder.doc.setTextColor(...COLORS.gray500);
  builder.doc.setFont('helvetica', 'normal');
  builder.doc.text('Technical White Paper', MARGIN, builder.currentY);
  builder.currentY += 5;
  
  builder.doc.setFontSize(9);
  builder.doc.text(`Generated ${data.timestamp}`, MARGIN, builder.currentY);
  builder.currentY += 4;
  
  // Brand line
  builder.doc.setFontSize(10);
  builder.doc.setTextColor(...COLORS.primary);
  builder.doc.setFont('helvetica', 'bold');
  builder.doc.text(`${data.serverDisplayName}  |  ${data.acceleratorDisplayName}`, PAGE_WIDTH - MARGIN, builder.currentY, { align: 'right' });
  builder.currentY += 10;
  
  // Divider
  builder.doc.setDrawColor(...COLORS.primaryDark);
  builder.doc.setLineWidth(1);
  builder.doc.line(MARGIN, builder.currentY, PAGE_WIDTH - MARGIN, builder.currentY);
  builder.currentY += 10;
  
  // Executive Summary
  builder.addSubheader('Executive Summary');
  builder.addMetricRow([
    { label: 'Servers', value: fmt(data.serverCount), highlight: true },
    { label: 'Total GPUs', value: fmt(data.gpuCount), highlight: true },
    { label: 'Concurrent Users', value: fmt(data.concurrentUsers) },
    { label: 'Limiting Constraint', value: data.bottleneck },
  ]);
  
  builder.addSpace(6);
  
  // Configuration tables
  builder.addSubheader('Model Configuration');
  builder.addKeyValueTable([
    { key: 'Model', value: data.modelId },
    { key: 'Parameters', value: `${data.paramsBillions}B` },
    { key: 'Precision', value: `${data.precisionBits}-bit` },
    { key: 'Batch Size', value: fmt(data.batchSize) },
  ]);
  
  builder.addSubheader('Workload Configuration');
  builder.addKeyValueTable([
    { key: 'Concurrent Users', value: fmt(data.concurrentUsers) },
    { key: 'Conversation Turns', value: fmt(data.conversationTurns) },
    { key: 'Latency Target', value: `${fmt(data.latencySloMs)} ms` },
    { key: 'User Personas', value: fmt(data.personaCount) },
  ]);
  
  builder.addSpace(6);
  
  // Table of Contents
  builder.addSubheader('Contents');
  const contents = [
    '1. Methodology Overview',
    '2. User Persona Analysis',
    '3. Throughput Calculations',
    '4. Memory Sizing',
    '5. Bottleneck Analysis',
    '6. Final Recommendations',
    '7. Advanced Insights & Analytics',
  ];
  if (data.hasCostData) {
    contents.push('8. Cloud Cost Comparison');
    contents.push('9. On-Premises TCO');
    contents.push('10. Cost Comparison Summary');
  }
  
  builder.doc.setFontSize(9);
  builder.doc.setTextColor(...COLORS.gray700);
  builder.doc.setFont('helvetica', 'normal');
  for (const item of contents) {
    builder.doc.text(item, MARGIN + 5, builder.currentY);
    builder.currentY += 5;
  }
}

function addMethodologySection(builder: PdfBuilder, snapshot: NewSizingSnapshot): void {
  builder.addSectionHeader('1', 'Methodology Overview', 'Monte Carlo simulation approach for realistic capacity planning');
  
  builder.addParagraph(
    'Our sizing methodology uses Monte Carlo simulation to model realistic user behavior patterns. ' +
    'Rather than assuming all users are active simultaneously, we simulate individual users following ' +
    'probabilistic state machines with read, think, and query phases.'
  );
  
  builder.addSpace(4);
  builder.addMetricRow([
    { label: 'Simulation Trials', value: fmt(snapshot.trials) },
    { label: 'Horizon', value: `${snapshot.horizonSec / 60} min` },
    { label: 'Sizing Percentile', value: 'P95', highlight: true },
  ]);
  
  builder.addSpace(4);
  builder.addSubheader('User State Machine');
  builder.addParagraph(
    'Each user cycles through three states with exponentially distributed durations: ' +
    'Reading (digest response) → Thinking (formulate query) → Generating (AI inference) → repeat.'
  );
  
  builder.addFormulaBlock([
    'λ = targetConcurrent / (readTime + thinkTime + serviceTime)',
    'cycleSec = readTime + thinkTime + serviceTime',
    'activeRatio = serviceTime / cycleSec',
  ]);
  
  builder.addSubheader('Why P95 Sizing?');
  builder.addParagraph(
    'We size infrastructure for the 95th percentile of concurrent demand. This ensures 95% of the time ' +
    'the system has adequate capacity without degradation, avoids costly over-provisioning for rare P99+ outliers, ' +
    'and balances cost efficiency with service level objectives.'
  );
}

function addPersonaSection(builder: PdfBuilder, request: NewSizingRequest, snapshot: NewSizingSnapshot): void {
  builder.addSectionHeader('2', 'User Persona Analysis', 'Characterizing user segments with unique usage patterns');
  
  builder.addSubheader('Persona Definition');
  builder.addFormulaBlock(['population = totalUsers × (sharePercent / 100)']);
  
  // Persona table
  const head = [['Persona', 'Share', 'Population', 'Input Tok', 'Output Tok', 'Read (s)', 'Think (s)']];
  const body = request.personas.map(p => [
    p.name,
    `${fmtDec(p.sharePercent, 1)}%`,
    fmt(Math.round(request.baseConcurrentUsers * (p.sharePercent / 100))),
    fmt(p.inputTokensPerConversation),
    fmt(p.outputTokensPerConversation),
    fmtDec(p.readSeconds, 0),
    fmtDec(p.thinkSeconds, 0),
  ]);
  
  builder.addTable(head, body, {
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
  });
  
  builder.addSpace(4);
  builder.addSubheader('Monte Carlo Session Distribution');
  builder.addMetricRow([
    { label: 'P50 Active Sessions', value: fmtDec(snapshot.p50Active, 1) },
    { label: 'P95 Active Sessions', value: fmtDec(snapshot.p95Active, 1), highlight: true },
  ]);
  
  builder.addSpace(4);
  builder.addSubheader('Percentile Distribution');
  const percentiles: Array<{ label: string; value: string; highlight?: boolean }> = [
    { label: 'P5', value: fmtDec(snapshot.activeByPercentile[5], 1) },
    { label: 'P25', value: fmtDec(snapshot.activeByPercentile[25], 1) },
    { label: 'P50', value: fmtDec(snapshot.activeByPercentile[50], 1) },
    { label: 'P75', value: fmtDec(snapshot.activeByPercentile[75], 1) },
    { label: 'P95', value: fmtDec(snapshot.activeByPercentile[95], 1), highlight: true },
  ];
  builder.addMetricRow(percentiles);
}

function addThroughputSection(
  builder: PdfBuilder,
  snapshot: NewSizingSnapshot,
  request: NewSizingRequest,
  narrativeData: SizingNarrativeData | null
): void {
  builder.addSectionHeader('3', 'Throughput Calculations', 'Token processing capacity requirements');
  
  builder.addSubheader('Required Throughput');
  builder.addParagraph(
    'Based on Monte Carlo simulation results, we calculate the tokens per second (TPS) required to serve P95 concurrent load.'
  );
  
  const promptTps = snapshot.avgPromptTokensPerSec;
  const decodeTps = snapshot.avgDecodeTokensPerSec;
  const promptGpus = snapshot.promptGpus;
  const decodeGpus = snapshot.decodeGpus;
  
  builder.addFormulaBlock([
    `promptTpsRequired = ${fmtDec(promptTps, 1)} tok/s`,
    `decodeTpsRequired = ${fmtDec(decodeTps, 1)} tok/s`,
  ]);
  
  builder.addSpace(4);
  builder.addSubheader('GPU Capacity');
  
  if (narrativeData?.serverConfig) {
    const sc = narrativeData.serverConfig;
    builder.addFormulaBlock([
      `promptTpsPerGpu = ${fmtDec(sc.rawPromptTpsPerGpu, 0)} × (1 - ${fmtDec(sc.throughputHeadroomPct * 100, 0)}%) = ${fmtDec(sc.promptTpsPerGpu, 0)} tok/s`,
      `decodeTpsPerGpu = ${fmtDec(sc.rawDecodeTpsPerGpu, 0)} × (1 - ${fmtDec(sc.throughputHeadroomPct * 100, 0)}%) = ${fmtDec(sc.decodeTpsPerGpu, 0)} tok/s`,
    ]);
  }
  
  builder.addSpace(4);
  builder.addMetricRow([
    { label: 'Prompt GPUs', value: fmtDec(promptGpus, 1) },
    { label: 'Decode GPUs', value: fmtDec(decodeGpus, 1) },
    { label: 'Throughput GPUs', value: fmtDec(Math.max(promptGpus, decodeGpus), 1), highlight: true },
  ]);
}

function addMemorySection(
  builder: PdfBuilder,
  request: NewSizingRequest,
  snapshot: NewSizingSnapshot,
  narrativeData: SizingNarrativeData | null
): void {
  builder.addSectionHeader('4', 'Memory Sizing', 'GPU memory allocation and KV cache requirements');
  
  const hbmGiB = request.model.hbmGiB ?? 96;
  const weightsGiB = request.model.weightsGiB ?? 0;
  const usableKvGiB = request.model.usableKvGiBPerGpu ?? 0;
  const runtimeGiB = hbmGiB - weightsGiB - usableKvGiB;
  const bridgeGroupSize = request.model.bridgeGroupSize ?? 1;
  
  builder.addSubheader('GPU Memory Allocation');
  builder.addFormulaBlock([
    `HBM per GPU: ${fmtDec(hbmGiB, 1)} GiB`,
    `Model Weights: ${fmtDec(weightsGiB, 1)} GiB (with TP=${bridgeGroupSize})`,
    `Runtime Overhead: ${fmtDec(runtimeGiB, 1)} GiB`,
    `Usable for KV Cache: ${fmtDec(usableKvGiB, 1)} GiB`,
  ]);
  
  // Memory breakdown bar (visual representation as text)
  builder.addSpace(4);
  const weightsPct = Math.round((weightsGiB / hbmGiB) * 100);
  const runtimePct = Math.round((runtimeGiB / hbmGiB) * 100);
  const kvPct = Math.round((usableKvGiB / hbmGiB) * 100);
  
  builder.addMetricRow([
    { label: `Weights (${weightsPct}%)`, value: `${fmtDec(weightsGiB, 1)} GiB` },
    { label: `Runtime (${runtimePct}%)`, value: `${fmtDec(runtimeGiB, 1)} GiB` },
    { label: `KV Cache (${kvPct}%)`, value: `${fmtDec(usableKvGiB, 1)} GiB`, highlight: true },
  ]);
  
  builder.addSpace(6);
  builder.addSubheader('KV Cache Sizing');
  
  const kvGpus = snapshot.kvGpusP95;
  const totalKvGiB = snapshot.kvGiBP95;
  
  builder.addFormulaBlock([
    `Total KV Required: ${fmtDec(totalKvGiB, 2)} GiB`,
    `GPUs for KV: ceil(${fmtDec(totalKvGiB, 2)} / ${fmtDec(usableKvGiB, 2)}) = ${fmtDec(kvGpus, 1)} GPUs`,
  ]);
  
  builder.addMetricRow([
    { label: 'Total KV GiB', value: fmtDec(totalKvGiB, 2) },
    { label: 'KV per GPU', value: `${fmtDec(usableKvGiB, 1)} GiB` },
    { label: 'KV GPUs Required', value: fmtDec(kvGpus, 1), highlight: true },
  ]);
}

function addBottleneckSection(
  builder: PdfBuilder,
  snapshot: NewSizingSnapshot,
  narrativeData: SizingNarrativeData | null
): void {
  builder.addSectionHeader('5', 'Bottleneck Analysis', 'Identifying the limiting constraint');
  
  const promptGpus = snapshot.promptGpus;
  const decodeGpus = snapshot.decodeGpus;
  const kvGpus = snapshot.kvGpusP95;
  const finalGpus = snapshot.finalGpuCount;
  
  const bottleneckLabel: Record<string, string> = {
    kv: 'KV Memory',
    prompt: 'Prompt Throughput',
    decode: 'Decode Throughput',
    none: 'None',
  };
  
  builder.addSubheader('GPU Requirements by Constraint');
  builder.addTable(
    [['Constraint', 'GPUs Required', 'Status']],
    [
      ['Prompt Throughput', fmtDec(promptGpus, 1), snapshot.finalBottleneck === 'prompt' ? '← LIMITING' : ''],
      ['Decode Throughput', fmtDec(decodeGpus, 1), snapshot.finalBottleneck === 'decode' ? '← LIMITING' : ''],
      ['KV Memory', fmtDec(kvGpus, 1), snapshot.finalBottleneck === 'kv' ? '← LIMITING' : ''],
    ],
    {
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'left' },
      },
    }
  );
  
  builder.addSpace(4);
  builder.addFormulaBlock([
    `finalGpuCount = max(promptGpus, decodeGpus, kvGpus)`,
    `finalGpuCount = max(${fmtDec(promptGpus, 1)}, ${fmtDec(decodeGpus, 1)}, ${fmtDec(kvGpus, 1)}) = ${finalGpus}`,
  ]);
  
  builder.addSpace(4);
  builder.addMetricRow([
    { label: 'Final GPU Count', value: fmt(finalGpus), highlight: true },
    { label: 'Limiting Constraint', value: bottleneckLabel[snapshot.finalBottleneck] ?? 'Unknown' },
  ]);
  
  // Utilization percentages
  if (narrativeData?.bottleneckDetail) {
    builder.addSpace(4);
    builder.addSubheader('Utilization at Final GPU Count');
    const bd = narrativeData.bottleneckDetail;
    builder.addMetricRow([
      { label: 'Prompt Util', value: `${fmtDec(bd.utilization.prompt * 100, 1)}%` },
      { label: 'Decode Util', value: `${fmtDec(bd.utilization.decode * 100, 1)}%` },
      { label: 'KV Util', value: `${fmtDec(bd.utilization.kv * 100, 1)}%` },
    ]);
  }
}

function addRecommendationsSection(
  builder: PdfBuilder,
  data: {
    serverCount: number;
    gpuCount: number;
    serverDisplayName: string;
    acceleratorDisplayName: string;
    bottleneck: string;
  }
): void {
  builder.addSectionHeader('6', 'Final Recommendations', 'Infrastructure deployment plan');
  
  builder.addSubheader('Hardware Requirements');
  builder.addTable(
    [['Component', 'Quantity', 'Details']],
    [
      ['Servers', fmt(data.serverCount), data.serverDisplayName],
      ['GPUs per Server', '8', data.acceleratorDisplayName],
      ['Total GPUs', fmt(data.gpuCount), ''],
    ],
    {
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'center' },
        2: { halign: 'left' },
      },
    }
  );
  
  builder.addSpace(6);
  builder.addSubheader('Summary');
  builder.addParagraph(
    `Based on our Monte Carlo simulation analysis, we recommend deploying ${data.serverCount} ${data.serverDisplayName} server(s) ` +
    `with a total of ${data.gpuCount} ${data.acceleratorDisplayName} accelerators. ` +
    `The primary limiting constraint is ${data.bottleneck}.`
  );
  
  builder.addSpace(4);
  builder.addSubheader('Next Steps');
  builder.addParagraph(
    '1. Validate assumptions with actual workload patterns if available.\n' +
    '2. Consider capacity for growth and seasonal variations.\n' +
    '3. Plan for redundancy and maintenance windows.\n' +
    '4. Evaluate total cost of ownership against cloud alternatives.'
  );
}

function addAdvancedInsightsSection(
  builder: PdfBuilder,
  snapshot: NewSizingSnapshot,
  narrativeData: SizingNarrativeData
): void {
  builder.addSectionHeader('7', 'Advanced Insights & Analytics', 'Detailed simulation metrics and calculations');
  
  // Monte Carlo statistics
  builder.addSubheader('Monte Carlo Simulation Statistics');
  builder.addTable(
    [['Metric', 'Value']],
    [
      ['Simulation Trials', fmt(snapshot.trials)],
      ['Horizon', `${snapshot.horizonSec / 60} minutes`],
      ['P50 Active Users', fmtDec(snapshot.p50Active, 2)],
      ['P95 Active Users', fmtDec(snapshot.p95Active, 2)],
      ['Base Concurrent Users', fmt(snapshot.baseConcurrentUsers)],
    ],
    { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
  );
  
  // Throughput breakdown
  builder.addSpace(4);
  builder.addSubheader('Throughput Breakdown');
  builder.addTable(
    [['Metric', 'Value']],
    [
      ['Avg Prompt TPS', fmtDec(snapshot.avgPromptTokensPerSec, 2)],
      ['Avg Decode TPS', fmtDec(snapshot.avgDecodeTokensPerSec, 2)],
      ['Prompt GPUs Required', fmtDec(snapshot.promptGpus, 2)],
      ['Decode GPUs Required', fmtDec(snapshot.decodeGpus, 2)],
    ],
    { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
  );
  
  // Memory breakdown
  builder.addSpace(4);
  builder.addSubheader('Memory Breakdown');
  if (narrativeData.gpuMemory) {
    const gm = narrativeData.gpuMemory;
    builder.addTable(
      [['Component', 'GiB']],
      [
        ['HBM per GPU', fmtDec(gm.hbmGiBPerGpu, 2)],
        ['Weights per GPU (with bridging)', fmtDec(gm.weightsGiBPerGpu, 2)],
        ['Weights per GPU (no bridging)', fmtDec(gm.weightsGiBUnshared, 2)],
        ['Runtime Overhead', fmtDec(gm.runtimeOverheadGiB, 2)],
        ['Usable KV per GPU', fmtDec(gm.usableKvGiBPerGpu, 2)],
        ['Memory Recaptured by Bridging', fmtDec(gm.memoryRecapturedGiB, 2)],
      ],
      { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
    );
  }
  
  // KV cache per persona
  if (narrativeData.personaKvBreakdowns && narrativeData.personaKvBreakdowns.length > 0) {
    builder.addSpace(4);
    builder.addSubheader('KV Cache by Persona');
    builder.addTable(
      [['Persona', 'Sessions P95', 'KV/Session (GiB)', 'Total KV (GiB)']],
      narrativeData.personaKvBreakdowns.map(p => [
        p.personaName,
        fmtDec(p.sessionsP95, 1),
        fmtDec(p.kvGiBPerSession, 4),
        fmtDec(p.totalKvGiB, 2),
      ]),
      {
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' },
        },
      }
    );
  }
}

function addCloudCostSection(
  builder: PdfBuilder,
  costData: NonNullable<PdfReportData['costData']>
): void {
  builder.addSectionHeader('8', 'Cloud Cost Comparison', 'Token-based cloud API pricing analysis');
  
  const { config, monthlyRollups } = costData;
  
  builder.addSubheader('Cloud API Configuration');
  builder.addTable(
    [['Parameter', 'Value']],
    [
      ['AI Service Model', config.aiServiceModel],
      ['Input Cost', `$${config.inputCostPerMToken} / 1M tokens`],
      ['Output Cost', `$${config.outputCostPerMToken} / 1M tokens`],
      ['Work Hours/Day', fmt(config.workHoursPerDay)],
      ['Work Days/Month', fmt(config.workDaysPerMonth)],
      ['Annual Growth Rate', `${fmt(config.annualGrowthRate)}%`],
    ],
    { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
  );
  
  // Monthly projections (first 12 months)
  builder.addSpace(4);
  builder.addSubheader('12-Month Cloud Cost Projection');
  
  const first12 = monthlyRollups.slice(0, 12);
  builder.addTable(
    [['Month', 'Input Tokens', 'Output Tokens', 'Monthly Cost', 'Cumulative']],
    first12.map(r => [
      r.monthKey,
      fmtTokens(r.inputTokens),
      fmtTokens(r.outputTokens),
      fmtCurrency(r.monthlyCloudCost, true),
      fmtCurrency(r.cumCloudUSD, true),
    ]),
    {
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
    }
  );
  
  // 5-year summary
  const lastMonth = monthlyRollups[monthlyRollups.length - 1];
  builder.addSpace(4);
  builder.addMetricRow([
    { label: 'Year 1 Cloud Cost', value: fmtCurrency(monthlyRollups[11]?.cumCloudUSD ?? 0, true) },
    { label: '5-Year Cloud Cost', value: fmtCurrency(lastMonth?.cumCloudUSD ?? 0, true), highlight: true },
  ]);
}

function addOnPremTcoSection(
  builder: PdfBuilder,
  costData: NonNullable<PdfReportData['costData']>,
  serverCount: number
): void {
  builder.addSectionHeader('9', 'On-Premises TCO', 'Total Cost of Ownership for on-prem infrastructure');
  
  const { config, monthlyRollups } = costData;
  const initialCapex = serverCount * config.capexPerServer;
  
  builder.addSubheader('Infrastructure Configuration');
  builder.addTable(
    [['Parameter', 'Value']],
    [
      ['Servers', fmt(serverCount)],
      ['GPUs per Server', fmt(config.gpusPerServer)],
      ['CapEx per Server', fmtCurrency(config.capexPerServer)],
      ['Total Initial CapEx', fmtCurrency(initialCapex)],
    ],
    { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
  );
  
  builder.addSpace(4);
  builder.addSubheader('Operating Expenses (Monthly)');
  
  // Calculate OpEx from first month data
  const firstMonth = monthlyRollups[0];
  builder.addTable(
    [['Category', 'Monthly Cost']],
    [
      ['Power (electricity)', fmtCurrency(firstMonth?.monthlyPowerCost ?? 0)],
      ['Administration', fmtCurrency(firstMonth?.monthlyAdminCost ?? 0)],
      ['Facilities', fmtCurrency(firstMonth?.monthlyFacilitiesCost ?? 0)],
      ['Personnel', fmtCurrency(firstMonth?.monthlyPersonnelCost ?? 0)],
      ['Total OpEx', fmtCurrency(firstMonth?.monthlyOpEx ?? 0)],
    ],
    { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
  );
  
  // 5-year TCO projection
  builder.addSpace(4);
  builder.addSubheader('5-Year TCO Projection');
  
  // Show year-end summaries
  const yearEnds = [11, 23, 35, 47, 59].map(i => monthlyRollups[i]).filter(Boolean);
  builder.addTable(
    [['Year', 'Cumulative CapEx', 'Cumulative OpEx', 'Total TCO']],
    yearEnds.map((r, i) => {
      const cumCapex = monthlyRollups.slice(0, r.monthIndex + 1).reduce((sum, m) => sum + m.upfrontCapex, 0);
      const cumOpex = r.cumOnPremTcoUSD - cumCapex;
      return [
        `Year ${i + 1}`,
        fmtCurrency(cumCapex, true),
        fmtCurrency(cumOpex, true),
        fmtCurrency(r.cumOnPremTcoUSD, true),
      ];
    }),
    {
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
    }
  );
  
  const lastMonth = monthlyRollups[monthlyRollups.length - 1];
  builder.addSpace(4);
  builder.addMetricRow([
    { label: 'Initial CapEx', value: fmtCurrency(initialCapex, true) },
    { label: '5-Year Total TCO', value: fmtCurrency(lastMonth?.cumOnPremTcoUSD ?? 0, true), highlight: true },
  ]);
}

function addCostComparisonSummary(
  builder: PdfBuilder,
  costData: NonNullable<PdfReportData['costData']>
): void {
  builder.addSectionHeader('10', 'Cost Comparison Summary', 'Cloud vs. On-Premises financial analysis');
  
  const { comparison, monthlyRollups } = costData;
  const lastMonth = monthlyRollups[monthlyRollups.length - 1];
  
  builder.addSubheader('5-Year Cost Comparison');
  builder.addTable(
    [['Deployment Model', '5-Year Total Cost']],
    [
      ['Cloud (API-based)', fmtCurrency(comparison.fiveYearCloudSpend)],
      ['On-Premises', fmtCurrency(comparison.fiveYearOnPremTco)],
    ],
    { columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' } } }
  );
  
  builder.addSpace(4);
  builder.addSubheader('Break-Even Analysis');
  
  if (comparison.breakEvenMonth) {
    builder.addParagraph(
      `On-premises infrastructure reaches cost parity with cloud at month ${comparison.breakEvenMonthIndex! + 1} (${comparison.breakEvenMonth}). ` +
      `After this point, on-premises deployment generates ongoing savings.`
    );
    
    builder.addMetricRow([
      { label: 'Break-Even Month', value: comparison.breakEvenMonth, highlight: true },
      { label: 'Months to Break-Even', value: fmt((comparison.breakEvenMonthIndex ?? 0) + 1) },
    ]);
  } else {
    builder.addParagraph(
      'On-premises infrastructure does not reach break-even within the 5-year analysis period. ' +
      'Cloud deployment may be more cost-effective for this workload profile.'
    );
  }
  
  builder.addSpace(4);
  builder.addSubheader('Savings Summary');
  
  const savingsSign = comparison.savings >= 0 ? '' : '-';
  const savingsAbs = Math.abs(comparison.savings);
  
  builder.addMetricRow([
    { 
      label: comparison.savings >= 0 ? '5-Year Savings (On-Prem)' : '5-Year Cost Premium (On-Prem)', 
      value: `${savingsSign}${fmtCurrency(savingsAbs, true)}`,
      highlight: true,
    },
    { 
      label: 'Savings Percentage', 
      value: `${fmtDec(Math.abs(comparison.savingsPercent), 1)}%`,
    },
  ]);
  
  builder.addSpace(6);
  builder.addSubheader('Recommendation');
  
  if (comparison.savings > 0 && (comparison.breakEvenMonthIndex ?? 60) < 24) {
    builder.addParagraph(
      'RECOMMENDED: On-premises deployment shows strong financial advantage with break-even in under 2 years. ' +
      'Consider proceeding with on-prem infrastructure investment.'
    );
  } else if (comparison.savings > 0) {
    builder.addParagraph(
      'On-premises deployment shows long-term cost advantage, but break-even takes more than 2 years. ' +
      'Consider hybrid approach or phased infrastructure investment.'
    );
  } else {
    builder.addParagraph(
      'Cloud deployment may be more cost-effective for this workload profile. ' +
      'Consider cloud for flexibility and reduced capital requirements.'
    );
  }
}

// Export filename generator
export function generatePdfFilename(prefix = 'ai-sizing-report'): string {
  return `${prefix}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
}
